import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import MessageButton from '../components/MessageButton';
import { useAuth } from '../context/AuthContext';

import { API_BASE_URL } from '../utils/api';
const API_BASE = API_BASE_URL;

interface Profile {
  id: number;
  userId: number;
  profession: string;
  rank?: string;
  department?: string;
  currentLocation: string;
  preferredLocations: string[];
  qualifications?: string;
  majors?: string;
  cvUrl?: string;
  verificationStatus: string;
  createdAt: string;
  updatedAt: string;
  User: {
    name: string;
    email: string;
  };
}

const TransferHubLanding: React.FC = () => {
  const { token } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [filter, setFilter] = useState({ profession: '', location: '' });
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState<string>('');

  const loadProfiles = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filter.profession) queryParams.append('profession', filter.profession);
      if (filter.location) queryParams.append('location', filter.location);

      const res = await fetch(`${API_BASE}/transferhub/profiles?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        setProfiles(data);
      }
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }
  }, [filter.location, filter.profession]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  

  const handleMessage = async (profile: Profile) => {
    if (!token) {
      setMessage('Please login to send messages');
      window.location.href = '/login';
      return;
    }
    window.location.href = '/transferhub/conversations';
  };

  const handleViewProfile = (profile: Profile) => {
    setSelectedProfile(profile);
    setOpenDialog(true);
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          TransferHub Profiles
        </Typography>
        <Typography variant="h6" color="textSecondary" sx={{ mb: 3 }}>
          Discover professionals looking for transfer opportunities
        </Typography>
      </Box>

      {message && <Alert sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {/* Search Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Profiles
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profession"
                value={filter.profession}
                onChange={(e) => setFilter({ ...filter, profession: e.target.value })}
                placeholder="e.g., Software Developer"
                InputProps={{
                  startAdornment: <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={filter.location}
                onChange={(e) => setFilter({ ...filter, location: e.target.value })}
                placeholder="e.g., Cape Town"
                InputProps={{
                  startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Profiles Grid */}
      {profiles.length === 0 ? (
        <Card>
          <CardContent>
            <Typography textAlign="center" color="textSecondary">
              No profiles found. Try adjusting your search filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {profiles.map((profile) => (
            <Grid item xs={12} md={6} lg={4} key={profile.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {profile.User.name}
                      </Typography>
                      <Chip 
                        label={profile.verificationStatus} 
                        size="small" 
                        color={profile.verificationStatus === 'verified' ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                      {profile.profession}
                    </Typography>
                    {profile.rank && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Rank: {profile.rank}
                      </Typography>
                    )}
                    {profile.department && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Department: {profile.department}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                      Current: {profile.currentLocation}
                    </Typography>
                    {profile.preferredLocations.length > 0 && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Preferred: {profile.preferredLocations.join(', ')}
                      </Typography>
                    )}
                  </Box>

                  {profile.qualifications && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                        {profile.qualifications}
                      </Typography>
                      {profile.majors && (
                        <Typography variant="body2" color="textSecondary">
                          Majors: {profile.majors}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewProfile(profile)}
                        >
                          View Profile
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        {token ? (
                          <MessageButton
                            receiverId={profile.userId}
                            receiverName={profile.User.name}
                            variant="contained"
                            size="small"
                            startIcon
                          />
                        ) : (
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<MessageIcon />}
                            onClick={() => handleMessage(profile)}
                          >
                            Message
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Profile Detail Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedProfile?.User.name}</Typography>
              <Chip 
                label={selectedProfile?.verificationStatus} 
                size="small" 
                color={selectedProfile?.verificationStatus === 'verified' ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom>
                  <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {selectedProfile.profession}
                </Typography>
                {selectedProfile.rank && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Rank:</strong> {selectedProfile.rank}
                  </Typography>
                )}
                {selectedProfile.department && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Department:</strong> {selectedProfile.department}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Location Information
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Current Location:</strong> {selectedProfile.currentLocation}
                </Typography>
                {selectedProfile.preferredLocations.length > 0 && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Preferred Locations:</strong> {selectedProfile.preferredLocations.join(', ')}
                  </Typography>
                )}
              </Grid>

              {(selectedProfile.qualifications || selectedProfile.majors) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Education
                  </Typography>
                  {selectedProfile.qualifications && (
                    <Typography variant="body1" gutterBottom>
                      <strong>Qualifications:</strong> {selectedProfile.qualifications}
                    </Typography>
                  )}
                  {selectedProfile.majors && (
                    <Typography variant="body1" gutterBottom>
                      <strong>Majors:</strong> {selectedProfile.majors}
                    </Typography>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Contact Information
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Email:</strong> {selectedProfile.User.email}
                </Typography>
                {selectedProfile.cvUrl && (
                  <Typography variant="body1" gutterBottom>
                    <strong>CV:</strong>{' '}
                    <a href={selectedProfile.cvUrl} target="_blank" rel="noopener noreferrer">
                      View CV
                    </a>
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
          {token && selectedProfile && (
            <MessageButton
              receiverId={selectedProfile.userId}
              receiverName={selectedProfile.User.name}
              variant="contained"
              size="medium"
              startIcon
            />
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferHubLanding;